import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Avatar, MCButton, MCText, SearchTextField } from 'src/components/basic';
import {
  remCss as rCss,
  useScreenDetail,
  lang,
  scssVariables,
  remFontCss as rFCss,
  userFullName
} from 'src/helpers';

import LANG_CONST from 'src/helpers/lang/const';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';

export const Categories = ({
  specialist,
  onClick,
  selected = [],
  showAllWhenNotSelected = false,
  onHandleMore = null,
  remFontCss = rFCss,
  remCss = rCss
}) => {
  const [categorySearch, setCategorySearch] = React.useState('');
  const [colAmount, setColAmount] = React.useState(8);
  const [more, setMore] = React.useState(false);
  const containerRef = useRef(null);
  const categoryRef = useRef(null);
  const screen = useScreenDetail();
  const showAll = showAllWhenNotSelected && !selected?.length;

  useEffect(() => {
    if (containerRef.current) {
      const dis = remCss(250);
      let col = Math.ceil((containerRef.current.clientWidth - remCss(30)) / remCss(dis));
      col =
        col * dis + 10 * (col - 1) > containerRef.current.clientWidth - remCss(30) ? col - 1 : col;

      if (col !== colAmount) {
        setColAmount(col);
      }
    }
  }, [colAmount, remCss, screen]);

  const openMore = useCallback(
    (v) => {
      setMore(v);
      onHandleMore && onHandleMore(v);
    },
    [onHandleMore]
  );

  const list = useMemo(() => {
    let nextList = [...(specialist || [])].sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a > b ? 1 : -1;
      } else if (typeof a === 'string') {
        return -1;
      }
      return 1;
    });
    if (categorySearch.toString().length > 0) {
      nextList = nextList.filter((s) =>
        typeof s === 'string'
          ? lang
              .t(LANG_CONST.constants.specialist[s])
              .toLowerCase()
              .indexOf(categorySearch?.toLowerCase()) > -1
          : s.label.toLowerCase().indexOf(categorySearch?.toLowerCase()) > -1
      );
    }
    return nextList;
  }, [categorySearch, specialist]);

  const needAll = colAmount < list.length;
  const isExpanded = showAll || more;

  return (
    <div className={'mc-flex-20-col'} id={'mc-categories-container'}>
      <div className={'mc-flex'}>
        <MCText
          label={LANG_CONST.titles.categories}
          size={24}
          weight={500}
          flex
          remFontCss={remFontCss}
        />

        <div className={'mc-flex'}>
          <SearchTextField
            template={'2'}
            placeholder={'Search'}
            onChange={setCategorySearch}
            value={categorySearch}
          />
        </div>
      </div>
      <div className={'mc-flex'} ref={containerRef}>
        {!isExpanded && needAll && (
          <MCButton
            light
            style={{ height: remCss(144), minWidth: remCss(64), borderRadius: remCss(10) }}
            tiny
            hover
            onClick={() => {
              if (categoryRef.current) {
                categoryRef.current.scrollTo({
                  behavior: 'smooth',
                  left: Math.max(0, categoryRef.current.scrollLeft - (colAmount / 3) * remCss(220))
                });
              }
            }}
          >
            <SVGIcon
              iconname={BASE_ICONS.LEFT}
              fill={scssVariables['colors-mc-primary']}
              width={remCss(40)}
            />
          </MCButton>
        )}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gap: remCss(15),
            ...(isExpanded
              ? {
                  gridTemplateColumns: `repeat(${colAmount}, minmax(${remCss(210)}px, 1fr))`
                }
              : {
                  gridAutoFlow: 'column',
                  gridTemplateColumns: `repeat(${list.length}, ${remCss(220)}px)`,
                  overflowX: 'hidden'
                })
          }}
          id={'mc-categories'}
          ref={categoryRef}
        >
          {list.map((s, index) => {
            let active = !!selected?.find((f) => s === f);
            return (
              <MCButton
                data-testid='category-button'
                shadow
                style={{
                  order: index + 1,
                  flexDirection: 'column',
                  boxShadow: `0 0 ${remCss(10)}px 0 rgba(0, 0, 0,  #0000000a)`,
                  borderRadius: remCss(10),
                  paddingVertical: remCss(15),
                  borderWidth: 2,
                  justifyContent: 'start',
                  ...(!isExpanded && index === list.length - 1 ? { marginRight: 1 } : {})
                }}
                primary={active}
                outline={active}
                onClick={(e) => {
                  e.preventDefault();
                  onClick(s);
                  openMore(false);
                  let behavior = isExpanded ? 'auto' : 'smooth';
                  setTimeout(() => {
                    e.target.scrollIntoView({
                      behavior,
                      block: 'center',
                      inline: 'center'
                    });
                  }, 0);
                }}
                className={'mc-category ' + (active ? 'active' : '')}
                key={'category-' + (s.label || s)}
              >
                {typeof s === 'string' ? (
                  <>
                    <SVGIcon iconname={s + '-icon'} />
                    <MCText
                      label={LANG_CONST.constants.specialist[s]}
                      size={20}
                      weight={500}
                      primary={active}
                      numberOfLines={isExpanded ? null : 1}
                      remFontCss={remFontCss}
                    />
                  </>
                ) : (
                  <>
                    <Avatar text={s.firstName} width={80} height={80} image={s.avatar} />
                    <MCText
                      label={userFullName(s, true)}
                      size={20}
                      weight={500}
                      primary={active}
                      numberOfLines={isExpanded ? null : 1}
                      remFontCss={remFontCss}
                    />
                  </>
                )}
              </MCButton>
            );
          })}
        </div>
        {!isExpanded && needAll && (
          <MCButton
            light
            style={{ height: remCss(144), minWidth: remCss(64), borderRadius: remCss(10) }}
            tiny
            hover
            onClick={() => {
              if (categoryRef.current) {
                categoryRef.current.scrollTo({
                  behavior: 'smooth',
                  left: Math.min(
                    categoryRef.current.scrollWidth + remCss(220),
                    categoryRef.current.scrollLeft + (colAmount / 3) * remCss(220)
                  )
                });
              }
            }}
          >
            <SVGIcon
              iconname={BASE_ICONS.RIGHT}
              fill={scssVariables['colors-mc-primary']}
              width={remCss(40)}
            />
          </MCButton>
        )}
      </div>

      <div className={'mc-flex'}>
        <div className={'mc-grow'} />
        <MCButton
          dark
          radius
          small
          primary
          disabled={!needAll}
          style={!needAll && { visibility: 'hidden' }}
          onClick={() => {
            if (isExpanded)
              setTimeout(() => {
                document.getElementById('mc-categories-container').scrollIntoView({
                  behavior: 'auto',
                  inline: 'center',
                  block: 'center'
                });
              }, 100);
            openMore(!isExpanded);
          }}
          label={
            !isExpanded ? LANG_CONST.buttons.more_categories : LANG_CONST.buttons.less_categories
          }
        />
      </div>
    </div>
  );
};
